import type { QueryClient } from '@tanstack/react-query';

import { getAuthState, useAuthStore } from '@/shared/auth/store';
import { usePresenceStore, type RealtimeManager } from '@/shared/realtime';

export interface SessionLifecycleOptions {
  queryClient: QueryClient;
  realtime: Pick<RealtimeManager, 'start' | 'stop'>;
  realtimeEnabled: boolean;
}

/**
 * Binds the app's per-user state to the auth session: realtime runs only while signed in; signing out drops all cached
 * data and live presence. Another account taking over without a sign-out in between (an invitation accepted while
 * signed in, a session that another tab started) is treated the same way: nothing of the previous user may remain —
 * cached lists, presence, or a hub connection that is still authenticated as them. Returns the unsubscribe function.
 */
export function bindSessionLifecycle({
  queryClient,
  realtime,
  realtimeEnabled,
}: SessionLifecycleOptions): () => void {
  let previousStatus = getAuthState().status;
  let previousUserId = getAuthState().user?.id ?? null;
  // A session the administrator policy restricts (set up two-factor authentication first) may not open the hub: the API
  // refuses it, and the connection would only retry until the restriction is lifted.
  const isRestricted = (state: ReturnType<typeof getAuthState>) =>
    state.status === 'authenticated' && !!state.user?.restriction;
  let previousRestricted = isRestricted(getAuthState());

  return useAuthStore.subscribe((state) => {
    const userId = state.status === 'authenticated' ? (state.user?.id ?? null) : null;
    const restricted = isRestricted(state);
    const restrictionChanged = restricted !== previousRestricted;
    previousRestricted = restricted;
    const switched =
      state.status === 'authenticated' &&
      previousStatus === 'authenticated' &&
      previousUserId !== null &&
      userId !== null &&
      userId !== previousUserId;
    const statusChanged = state.status !== previousStatus;
    const wasSignedIn = previousStatus === 'authenticated';
    previousStatus = state.status;
    previousUserId = userId;

    if (switched) {
      queryClient.clear();
      usePresenceStore.getState().reset();
      if (realtimeEnabled) {
        void realtime.stop().then(() => {
          if (getAuthState().status === 'authenticated' && !getAuthState().user?.restriction)
            realtime.start();
        });
      }
      return;
    }

    if (!statusChanged) {
      // Still signed in, but the restriction came or went (the policy was enforced, the step was completed).
      if (realtimeEnabled && restrictionChanged) {
        if (restricted) void realtime.stop();
        else realtime.start();
      }
      return;
    }

    if (state.status === 'authenticated') {
      if (realtimeEnabled && !restricted) realtime.start();
    } else if (state.status === 'anonymous') {
      void realtime.stop();
      usePresenceStore.getState().reset();
      // Only a session that ends leaves data of a user behind. At the start the check of the refresh cookie finds nobody
      // (`unknown` → `anonymous`) and there is nothing to drop — but a public page (the reset-password link, the invitation)
      // may already have its own request in flight: clearing then removes the query under it, and the page waits for an
      // answer that nobody delivers (a skeleton for good).
      if (wasSignedIn) queryClient.clear();
    }
  });
}
